import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';

interface Props {
  onBack: () => void;
}

export default function FleetManagerLogin({ onBack }: Props) {
  const { signIn, signOut, resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot password
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { error: signInError, role } = await signIn(email, password);

    if (signInError) {
      setError(signInError);
      setLoading(false);
      return;
    }

    // Verify fleet_manager role
    if (role !== 'fleet_manager' && role !== 'ceo_admin') {
      setError('Accès refusé. Ce compte n\'a pas les droits Fleet Manager.');
      await signOut();
      setLoading(false);
      return;
    }

    setLoading(false);
    // RoleContext will auto-update via onAuthStateChange
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMsg('');
    const { error: resetError } = await resetPassword(forgotEmail);
    setForgotMsg(resetError ?? 'Email envoyé ! Vérifiez votre boîte mail.');
    setForgotLoading(false);
  };

  const bgStyle = { background: 'linear-gradient(160deg, #0f172a 0%, #064e3b 50%, #065f46 100%)' };

  if (showForgot) {
    return (
      <div className="fixed inset-0 z-[997] flex flex-col overflow-hidden" style={bgStyle}>
        <div className="pt-12 px-5 pb-2">
          <button onClick={() => { setShowForgot(false); setForgotMsg(''); setForgotEmail(''); }} className="flex items-center gap-2 text-emerald-300 text-sm font-medium">
            <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-xl">
              <i className="ri-arrow-left-line text-white text-sm"></i>
            </div>
            <span>Retour</span>
          </button>
        </div>
        <div className="px-6 pt-4 pb-6 text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-xl" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
            <i className="ri-mail-send-line text-white text-2xl"></i>
          </div>
          <h1 className="text-white font-extrabold text-xl mb-1">Mot de passe oublié</h1>
          <p className="text-emerald-300 text-sm">Entrez votre email Fleet Manager</p>
        </div>
        <div className="flex-1 px-5">
          <div className="rounded-3xl p-5" style={{ background: 'rgba(255,255,255,0.07)', border: '1.5px solid rgba(255,255,255,0.12)' }}>
            <form onSubmit={handleForgot} className="space-y-4">
              <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 ${forgotEmail ? 'border-2 border-emerald-400/60 bg-emerald-500/10' : 'border border-white/15 bg-white/5'}`}>
                <i className="ri-mail-line text-white/40 text-sm"></i>
                <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="votre@email.com" className="flex-1 text-sm text-white outline-none bg-transparent placeholder-white/25" />
              </div>
              {forgotMsg && (
                <div className={`rounded-xl p-3 text-xs font-medium ${forgotMsg.includes('envoyé') ? 'bg-emerald-500/15 border border-emerald-500/25 text-emerald-300' : 'bg-red-500/15 border border-red-500/25 text-red-300'}`}>
                  {forgotMsg}
                </div>
              )}
              <button type="submit" disabled={!forgotEmail || forgotLoading} className="w-full py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-40" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                <i className={`${forgotLoading ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} text-white text-sm`}></i>
                <span className="text-white">{forgotLoading ? 'Envoi...' : 'Envoyer le lien'}</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[997] flex flex-col overflow-hidden" style={bgStyle}>
      <div className="absolute top-[-60px] right-[-50px] w-56 h-56 rounded-full pointer-events-none" style={{ background: 'rgba(16,185,129,0.08)' }} />
      <div className="pt-12 px-5 pb-2 relative z-10">
        <button onClick={onBack} className="flex items-center gap-2 text-emerald-300 text-sm font-medium">
          <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-xl">
            <i className="ri-arrow-left-line text-white text-sm"></i>
          </div>
          <span>Changer de profil</span>
        </button>
      </div>
      <div className="px-6 pt-4 pb-6 text-center relative z-10">
        <div className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-2xl" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
          <i className="ri-shield-keyhole-line text-white" style={{ fontSize: '2.2rem' }}></i>
        </div>
        <p className="text-emerald-400 text-[10px] font-bold uppercase tracking-widest mb-0.5">AutoLink</p>
        <h1 className="text-white font-extrabold text-xl mb-1">Fleet Manager</h1>
        <p className="text-emerald-300 text-sm">Console d&apos;administration sécurisée</p>
      </div>
      <div className="flex-1 px-5 relative z-10">
        <div className="rounded-3xl p-5" style={{ background: 'rgba(255,255,255,0.07)', border: '1.5px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(12px)' }}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-emerald-300 uppercase tracking-wide mb-1.5">Email</label>
              <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 ${email ? 'border-2 border-emerald-400/60 bg-emerald-500/10' : 'border border-white/15 bg-white/5'}`}>
                <i className="ri-mail-line text-white/40 text-sm"></i>
                <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError(''); }} placeholder="votre@email.com" autoComplete="email" className="flex-1 text-sm text-white outline-none bg-transparent placeholder-white/25" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-emerald-300 uppercase tracking-wide mb-1.5">Mot de passe</label>
              <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 ${password ? 'border-2 border-emerald-400/60 bg-emerald-500/10' : 'border border-white/15 bg-white/5'}`}>
                <i className="ri-lock-line text-white/40 text-sm"></i>
                <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => { setPassword(e.target.value); setError(''); }} placeholder="••••••••••" autoComplete="current-password" className="flex-1 text-sm text-white outline-none bg-transparent placeholder-white/25" />
                <button type="button" onClick={() => setShowPwd(v => !v)} className="w-5 h-5 flex items-center justify-center">
                  <i className={`${showPwd ? 'ri-eye-off-line' : 'ri-eye-line'} text-white/30 text-base`}></i>
                </button>
              </div>
            </div>
            {error && (
              <div className="flex items-start gap-2 bg-red-500/15 border border-red-500/25 rounded-xl p-3">
                <i className="ri-error-warning-line text-red-400 text-sm flex-shrink-0 mt-0.5"></i>
                <p className="text-xs text-red-300 font-medium leading-snug">{error}</p>
              </div>
            )}
            <button type="submit" disabled={!email || !password || loading} className="w-full py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 mt-2 disabled:opacity-40" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
              <i className={`${loading ? 'ri-loader-4-line animate-spin' : 'ri-login-box-line'} text-white text-sm`}></i>
              <span className="text-white">{loading ? 'Vérification...' : 'Accéder à la console'}</span>
            </button>
          </form>
        </div>
        <div className="mt-4 rounded-2xl p-3 flex items-start gap-2" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <i className="ri-information-line text-emerald-400 text-sm flex-shrink-0 mt-0.5"></i>
          <p className="text-[10px] text-emerald-300 leading-snug">Vos identifiants vous ont été transmis par l&apos;administrateur système.</p>
        </div>
        <button onClick={() => setShowForgot(true)} className="w-full mt-3 text-center text-white/40 text-xs py-2">
          Mot de passe oublié ? <span className="text-emerald-300 underline">Réinitialiser</span>
        </button>
      </div>
    </div>
  );
}
